'use client'

import * as React from 'react'
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SelectOption<T = string> {
  label: string
  value: T
  icon?: ReactNode
}

export interface SelectProps<T = string> {
  value?: T | T[]
  onChange?: (value: T | T[]) => void
  options: SelectOption<T>[]
  placeholder?: string
  searchable?: boolean
  multiple?: boolean
  disabled?: boolean
  className?: string
  /** Render the dropdown panel into document.body via a portal so it escapes
   * ancestor `overflow:hidden` containers (e.g. Modal). Off by default to
   * preserve existing layout behavior for every other caller. */
  portal?: boolean
  /** Trigger size. 'md' (default) keeps the original px-4 py-2 look; 'sm' is a
   * compact 34px-tall trigger for dense toolbars (matches ThemeSwitcher). */
  size?: 'sm' | 'md'
}

function SelectInner<T extends string | number>(
  {
    value,
    onChange,
    options,
    placeholder = 'Select...',
    searchable = false,
    multiple = false,
    disabled = false,
    className,
    portal = false,
    size = 'md',
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  // Portal positioning: anchored to the trigger's viewport rect. Recomputed on
  // open, resize, scroll. Only used when `portal` is true.
  const [portalPos, setPortalPos] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  } | null>(null)

  const updatePortalPosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    // Flip the panel above the trigger when there isn't room below it (e.g. a
    // Select pinned to the bottom of the screen). max-h-60 (240px) + a little
    // for the optional search box / padding.
    const MENU_MAX = 264
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openUp = spaceBelow < MENU_MAX && spaceAbove > spaceBelow
    setPortalPos({
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 } // anchor above, grows up
        : { top: rect.bottom + 4 }), // 4px gap, matches the inline mt-1
    })
  }, [])

  useEffect(() => {
    if (!isOpen || !portal) return
    updatePortalPosition()
    window.addEventListener('resize', updatePortalPosition)
    window.addEventListener('scroll', updatePortalPosition, true)
    return () => {
      window.removeEventListener('resize', updatePortalPosition)
      window.removeEventListener('scroll', updatePortalPosition, true)
    }
  }, [isOpen, portal, updatePortalPosition])

  // Tag the portaled panel so we can recognise clicks inside it as "inside" the
  // select. Without this, the click on a dropdown option would close the menu
  // before the option's onClick fires (when rendered through a portal).
  const panelRef = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node
    if (containerRef.current && containerRef.current.contains(target)) return
    if (panelRef.current && panelRef.current.contains(target)) return
    setIsOpen(false)
    setSearch('')
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus()
    }
  }, [isOpen, searchable])

  const filteredOptions = searchable && search
    ? options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()))
    : options

  const selectedValues = multiple
    ? (Array.isArray(value) ? value : [])
    : (value !== undefined ? [value] : [])

  const isSelected = (optValue: T) => selectedValues.includes(optValue)

  const handleSelect = (optValue: T) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : []
      const next = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue]
      onChange?.(next as T | T[])
    } else {
      onChange?.(optValue)
      setIsOpen(false)
      setSearch('')
    }
  }

  const displayLabel = () => {
    if (multiple) {
      const count = selectedValues.length
      if (count === 0) return placeholder
      if (count === 1) {
        const opt = options.find((o) => o.value === selectedValues[0])
        return opt?.label || placeholder
      }
      return `${count} selected`
    }
    const opt = options.find((o) => o.value === value)
    return opt ? (
      <span className="flex items-center gap-2">
        {opt.icon}
        {opt.label}
      </span>
    ) : placeholder
  }

  // Combined ref so both `ref` (forwarded) and `triggerRef` (internal) point to
  // the trigger button. The forwarded ref keeps the public API stable.
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      // Internal ref for portal positioning.
      ;(triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
      // Forward to the public ref. The forwarded ref is typed as HTMLDivElement
      // upstream but we attach the trigger button — cast is intentional.
      if (typeof ref === 'function') {
        ;(ref as (n: HTMLButtonElement | null) => void)(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
      }
    },
    [ref],
  )

  const panelStyle: React.CSSProperties = portal
    ? {
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        position: 'fixed',
        left: portalPos?.left ?? 0,
        width: portalPos?.width ?? undefined,
        ...(portalPos?.bottom != null
          ? { bottom: portalPos.bottom }
          : { top: portalPos?.top ?? 0 }),
        zIndex: 1000,
      }
    : {
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }

  const panelClassName = portal
    ? 'rounded-lg shadow-xl overflow-hidden'
    : 'absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-50 overflow-hidden'

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={setTriggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'glass-card rounded-lg flex items-center gap-2 w-full justify-between transition-all duration-200 text-sm',
          size === 'sm' ? 'px-2.5 h-[34px]' : 'px-4 py-2',
          disabled && 'opacity-50 cursor-not-allowed',
          isOpen && 'ring-2 ring-[var(--color-lime-muted)]',
        )}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        <span className={cn('truncate', selectedValues.length === 0 && 'text-muted-foreground')}>
          {displayLabel()}
        </span>
        <ChevronDown className={cn('w-4 h-4 flex-shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (() => {
        // When portal is true, render the panel into document.body so it isn't
        // clipped by ancestor overflow:hidden (Modal in particular).
        const panel = (
          <div
            ref={panelRef}
            className={panelClassName}
            style={panelStyle}
            // The panel may be portaled to document.body (outside any parent
            // popover's DOM subtree). Stop mousedown from bubbling so a parent
            // click-outside handler (e.g. UserMenu) doesn't close and unmount
            // the Select before an option's onClick lands.
            onMouseDown={(e) => e.stopPropagation()}
          >
          {searchable && (
            <div className="p-2 border-b border-[var(--color-border)]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-transparent border-0 focus:outline-none placeholder:text-muted-foreground"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="py-1 max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors',
                    isSelected(opt.value)
                      ? 'text-[var(--color-lime)] font-medium'
                      : 'text-foreground',
                  )}
                  style={{
                    backgroundColor: isSelected(opt.value) ? 'var(--color-surface-hover)' : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected(opt.value)) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected(opt.value)) {
                      e.currentTarget.style.backgroundColor = ''
                    }
                  }}
                >
                  {multiple && (
                    <div className={cn(
                      'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all',
                      isSelected(opt.value)
                        ? 'bg-[var(--color-lime)] border-[var(--color-lime)] text-[var(--primary-foreground)]'
                        : 'border-muted-foreground',
                    )}>
                      {isSelected(opt.value) && <Check className="w-3 h-3" />}
                    </div>
                  )}
                  {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                  <span className="truncate">{opt.label}</span>
                  {!multiple && isSelected(opt.value) && (
                    <Check className="w-4 h-4 ml-auto flex-shrink-0 text-[var(--color-lime)]" />
                  )}
                </button>
              ))
            )}
          </div>
          </div>
        )

        if (portal && typeof document !== 'undefined') {
          return createPortal(panel, document.body)
        }
        return panel
      })()}
    </div>
  )
}

const Select = React.forwardRef(SelectInner) as <T extends string | number>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement

export { Select }
